import {_} from 'lodash';
import * as ko from 'knockout';
import {ObjectTypeId} from '../../../../constants';
import {IObjectData, TSyncfield} from '../../../../index';
import {getRouteDefinition} from '../../../../constants/RouteDefinitions';
import {FilterType, IObjectSearchParams, template as tpl, viewModel as ObjectSearch} from '../../../object/search';
import {nsSearchToggle} from '../../../search/nsSearchToggle';
import ItemCheckbox = nsSearchToggle.ItemCheckbox;

export const template = tpl;

/** Syncfields required on each search hit for `createoverride` (not shown as columns). */
const OVERRIDE_TARGET_SYNCFIELD_NAMES: TSyncfield[] = ['SourceId'];

export interface IAddMembersSearchParams extends IObjectSearchParams {
    overrideTargetUserIds: number[];
    selectedObjects: KnockoutObservableArray<IObjectData>;
}

/** User search with checkboxes; override targets are visible but not selectable. */
export class viewModel extends ObjectSearch {
    private readonly overrideTargetUserIds: number[];

    constructor(params: IAddMembersSearchParams) {
        super(_.defaultsDeep(params, {
            autoSelect: true,
            fnValidateRow: (obj: IObjectData) => !params.overrideTargetUserIds?.includes(obj.id),
            isSelectable: true,
            syncfieldNames: OVERRIDE_TARGET_SYNCFIELD_NAMES,
            __routeDef: getRouteDefinition('usersearch', {showHeading: false}),
            __objectTypeId: ObjectTypeId.User,
        }));
        this.overrideTargetUserIds = params.overrideTargetUserIds ?? [];
    }

    protected override getTableKeys(): string[] {
        const keys = super.getTableKeys();
        const extra = this.params.syncfieldNames ?? OVERRIDE_TARGET_SYNCFIELD_NAMES;
        return _.uniq([...keys, ...extra]);
    }

    override showTableColumn(key: string): boolean {
        if (OVERRIDE_TARGET_SYNCFIELD_NAMES.includes(key as TSyncfield)) {
            return false;
        }
        return super.showTableColumn(key);
    }

    protected override onRowClick(item: IObjectData, e: JQuery.Event) {
        if (this.params.fnValidateRow && !this.params.fnValidateRow(item)) {
            return;
        }
        this.getItemCheckbox(item).toggle();
    }

    override isFilterAvailable(filterType: FilterType) {
        if (filterType === FilterType.Deleted) {
            return false;
        }
        return super.isFilterAvailable(filterType);
    }

    override showOperationsButton(): boolean {
        return false;
    }

    protected override showSelectedList = ko.pureComputed((): boolean => false);

    private hasOverrideTarget(obj: IObjectData): boolean {
        return this.overrideTargetUserIds.includes(obj.id);
    }

    protected override getRowBindings(obj: IObjectData): any {
        return this.hasOverrideTarget(obj)
            ? {class: 'rowDisabled'}
            : super.getRowBindings(obj);
    }

    protected override getCellTitle(key: string, obj: IObjectData): string | number | null {
        return key !== '$derivedFromManualObject' && this.hasOverrideTarget(obj)
            ? 'group.members.add.has.override'
            : super.getCellTitle(key, obj);
    }

    /** Override targets keep a visible row with a disabled selector checkbox. */
    protected override getItemCheckbox(item: IObjectData): ItemCheckbox<IObjectData> {
        const checkbox = super.getItemCheckbox(item);
        if (this.params.fnValidateRow && !this.params.fnValidateRow(item)) {
            checkbox.isEnabled = () => false;
            checkbox.getTitle = () => 'group.members.add.has.override';
        }
        return checkbox;
    }
}
